import React from 'react';
import { motion } from 'framer-motion';
import { ExclamationTriangleIcon, FireIcon, ShieldExclamationIcon, ClockIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface AlertCenterDemoProps extends TeaserDemoProps {
  alerts?: Array<{
    id: string;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    type: 'PERFORMANCE' | 'RISK' | 'SYSTEM';
    title: string;
    description: string;
    timestamp: string;
    isNew?: boolean;
  }>;
}

const AlertCenterDemoComponent: React.FC<AlertCenterDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  alerts = [
    {
      id: '1', 
      priority: 'HIGH', 
      type: 'PERFORMANCE', 
      title: 'Strategy Underperforming',
      description: 'Beta Trader below target for 3 days',
      timestamp: '2 min ago',
      isNew: true
    },
    {
      id: '2', 
      priority: 'MEDIUM', 
      type: 'RISK', 
      title: 'Risk Threshold Exceeded',
      description: 'Portfolio exposure at 89%',
      timestamp: '5 min ago'
    },
    {
      id: '3', 
      priority: 'HIGH', 
      type: 'SYSTEM', 
      title: 'Account Rebalance Required',
      description: 'Elite Scalper allocation drift',
      timestamp: '8 min ago',
      isNew: true
    }
  ]
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'HIGH': return FireIcon;
      case 'MEDIUM': return ExclamationTriangleIcon;
      case 'LOW': return ShieldExclamationIcon;
      default: return ExclamationTriangleIcon;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'text-red-400 bg-red-500/20 border-red-500/40';
      case 'MEDIUM': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/40';
      case 'LOW': return 'text-blue-400 bg-blue-500/20 border-blue-500/40';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/40';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PERFORMANCE': return '📈';
      case 'RISK': return '⚠️';
      case 'SYSTEM': return '⚙️';
      default: return '📊';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 5, -5, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <ExclamationTriangleIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Alert Center</h4>
          <p className="text-gray-300 text-xs">{alerts.filter(a => a.isNew).length} new alerts</p>
        </div>
      </div>

      {/* Alert Items */}
      <div className="space-y-2 flex-1">
        {alerts.map((alert, index) => {
          const PriorityIcon = getPriorityIcon(alert.priority);
          return (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className={`relative p-3 rounded-lg border backdrop-blur-sm ${getPriorityColor(alert.priority)}`}
            >
              {/* New Alert Indicator */}
              {alert.isNew && (
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
                />
              )}

              <div className="flex items-start gap-3">
                <div className="flex items-center gap-2 mt-0.5">
                  <PriorityIcon className="w-4 h-4" />
                  <span className="text-xs">{getTypeIcon(alert.type)}</span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h5 className="text-white text-sm font-medium truncate">
                      {alert.title}
                    </h5>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      {alert.timestamp}
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    {alert.description}
                  </p>
                </div>
              </div>

              {/* Priority Badge */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.15 + 0.3 }}
                className="absolute top-2 left-2"
              >
                <span className={`
                  inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium
                  ${alert.priority === 'HIGH' ? 'bg-red-500/30 text-red-300' : 
                    alert.priority === 'MEDIUM' ? 'bg-yellow-500/30 text-yellow-300' :
                    'bg-blue-500/30 text-blue-300'}
                `}>
                  {alert.priority}
                </span>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Status Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-3 flex items-center justify-center gap-4 text-xs"
      >
        <div className="flex items-center gap-1">
          <motion.div
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-2 h-2 bg-red-400 rounded-full"
          />
          <span className="text-red-300">{alerts.filter(a => a.priority === 'HIGH').length} High</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-yellow-400 rounded-full" />
          <span className="text-yellow-300">{alerts.filter(a => a.priority === 'MEDIUM').length} Medium</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const AlertCenterDemo = createOptimizedTeaserDemo(
  AlertCenterDemoComponent,
  'AlertCenterDemo'
);